import { Observable, Observer, forkJoin } from 'rxjs';
import { generateRandomGUID } from './guid.utilities';

/**
 * Utility class for file chunking operations
 * Used by both normal and external document upload services
 */
export class FileChunkingUtilities {
  private static readonly CHUNK_SIZE = 500000; // 500KB

  /**
   * Splits a file into chunks and uploads them
   * @param file The file to upload
   * @param uploadPartFn Function to upload a single chunk
   * @param completeUploadFn Function to complete the upload
   * @param isAttachment Whether this is an attachment
   * @param isAccountLogo Whether this is an account logo
   * @param compressAndProcessFn Optional function to compress and process the file (for images)
   */
  static splitAndUploadFile(
    file: File,
    uploadPartFn: (part: number, fileName: string, file: File) => Observable<void>,
    completeUploadFn: (originalFileName: string, newFileName: string, isAttachment: boolean, isAccountLogo: boolean) => Observable<string>,
    isAttachment: boolean = true,
    isAccountLogo: boolean = false,
    compressAndProcessFn?: (fileName: string, base64Data: string, isAttachment: boolean, isAccountLogo: boolean) => Observable<string>
  ): Observable<string> {
    return new Observable<string>((observer: Observer<string>) => {
      const reader = new FileReader();
      reader.onload = (ev: ProgressEvent<FileReader>) => {
        // If compression is requested and a compression function is provided
        if (compressAndProcessFn) {
          compressAndProcessFn(file.name, ev.target.result as string, isAttachment, isAccountLogo).subscribe((res) => {
            observer.next(res);
          });
        } else {
          this.readAndUploadChunks(
            ev.target.result as string,
            file.name,
            isAttachment,
            isAccountLogo,
            uploadPartFn,
            completeUploadFn,
            observer
          );
        }
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Reads chunks and uploads them (public for compression use case)
   */
  static readAndUploadChunks(
    toBeChunked: string,
    fileName: string,
    isAttachment: boolean,
    isAccountLogo: boolean,
    uploadPartFn: (part: number, fileName: string, file: File) => Observable<void>,
    completeUploadFn: (originalFileName: string, newFileName: string, isAttachment: boolean, isAccountLogo: boolean) => Observable<string>,
    observer: Observer<string>
  ): void {
    const chunkedFileParts = this.readAllChunks(toBeChunked);

    this.uploadChunksAndFinalise(fileName, chunkedFileParts, uploadPartFn).subscribe(() => {
      const newFileName = generateRandomGUID();
      completeUploadFn(fileName, newFileName, isAttachment, isAccountLogo).subscribe((fileName) => {
        observer.next(fileName);
      });
    });
  }

  private static readAllChunks(base64str: string): Array<string> {
    const base64result = base64str.substr(base64str.indexOf(',') + 1);
    const chunkedFileParts: string[] = [];

    for (let i = 0; i < base64result.length; i = i + this.CHUNK_SIZE) {
      const part = this.readChunk(base64result, i, i + this.CHUNK_SIZE);
      chunkedFileParts.push(part);
    }

    return chunkedFileParts;
  }

  private static readChunk(file: string, start: number, end: number): string {
    const blob = file.slice(start, end);
    return blob.toString();
  }

  private static uploadChunksAndFinalise(
    imageName: string,
    chunkedFileParts: Array<string>,
    uploadPartFn: (part: number, fileName: string, file: File) => Observable<void>
  ): Observable<boolean> {
    return new Observable<boolean>((observer: Observer<boolean>) => {
      const observableBatch: Observable<void>[] = [];
      let count = 0;
      chunkedFileParts.forEach((chunk) => {
        const bytes = this.getBytesFromChunk(chunk);
        const temp = new File([bytes as unknown as BlobPart], 'temp.tmp', { type: 'application/octet-stream' });
        observableBatch.push(uploadPartFn(count + 1, imageName, temp));
        count += 1;
      });

      forkJoin(observableBatch).subscribe(() => {
        observer.next(true);
      });
    });
  }

  private static getBytesFromChunk(chunk: string): Uint8Array {
    const bytes = new Uint8Array(chunk.length);
    for (let t = 0; t < chunk.length; t++) {
      bytes[t] = chunk.charCodeAt(t);
    }
    return bytes;
  }
}
